import React, { FC } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import { Button, Text } from "@epignosis_llc/gnosis";
import { useResponsive } from "ahooks";
import { AxiosError } from "axios";

import { handleNoPermissionError } from "@errors";

import { putActiveTheme } from "@api/themes";
import { useApplyTranslations } from "@hooks";
import { useUIStore } from "@stores";
import { generalNotification } from "@utils/helpers";

import queryKeys from "@constants/queryKeys";
import { URLS } from "@constants/urls";

import NotificationBanner from "@components/ReusableComponents/NotificationBanner/NotificationBanner";

const PreviewThemeNotification: FC = () => {
  const { t } = useApplyTranslations();
  const { lg } = useResponsive();
  const queryClient = useQueryClient();
  const { selectedCustomTheme, setSelectedCustomTheme, setThemePreviewMode } = useUIStore();
  const navigate = useNavigate();

  const { mutate: putActiveThemeMutation } = useMutation(queryKeys.themes.active, putActiveTheme, {
    onSuccess: () => {
      const { name } = selectedCustomTheme ?? {};
      generalNotification(
        "success",
        <span
          dangerouslySetInnerHTML={{
            __html: t("themes.setActiveSuccessfully", { name }),
          }}
        />,
      );

      queryClient.invalidateQueries([queryKeys.domainSettings]);
      queryClient.invalidateQueries([
        queryKeys.accountAndSettings.portalSettings.getPortalSettings,
      ]);
      queryClient.invalidateQueries([queryKeys.themes.themes]);
      setSelectedCustomTheme(null);
    },
    onError: (error: AxiosError) => {
      handleNoPermissionError(error);
    },
  });

  const handleSetThemeActive = (): void => {
    if (!selectedCustomTheme) return;
    putActiveThemeMutation(selectedCustomTheme.id);
    setThemePreviewMode(false);
  };

  const navigateToThemeDrawer = (): void => {
    navigate(`${URLS.accountAndSettings.accountAndSettings}?tab=portalSettings&drawer=theme`);
    setSelectedCustomTheme(null);
    setThemePreviewMode(false);
  };

  const ActionButtons: FC = () => (
    <>
      <Button
        data-testid="apply-theme-button"
        aria-label={t("general.apply")}
        variant="link"
        size="sm"
        underlined
        onClick={handleSetThemeActive}
      >
        {t("general.apply")}
      </Button>
      <Text fontSize="xs">{t("signUp.or")}</Text>
      <Button
        data-testid="edit-theme-button"
        aria-label={t("themes.backToEditing")}
        variant="link"
        size="sm"
        underlined
        onClick={navigateToThemeDrawer}
      >
        {t("themes.backToEditing")}
      </Button>
    </>
  );

  return (
    <NotificationBanner
      title={t("themes.previewMode")}
      description={lg ? t("themes.previewDescription") : ""}
      ariaLabel={t("themes.previewDescription")}
      actions={<ActionButtons />}
      variant="warning"
    />
  );
};

export default PreviewThemeNotification;
